import SentryService from '@services/SentryService';

import type { StorageBackendType } from './types';

const OPFS_FAILURE_KEY = 'jotpsych_opfs_runtime_failure';
const OPFS_FAILURE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Persist an OPFS runtime failure timestamp so we skip OPFS on next load. */
export function markOPFSRuntimeFailure(): void {
  try {
    localStorage.setItem(OPFS_FAILURE_KEY, String(Date.now()));
  } catch {
    // localStorage may be unavailable (private browsing, full) — ignore
  }
}

/** Clear the OPFS failure flag (e.g. after a successful OPFS session). */
export function clearOPFSRuntimeFailure(): void {
  try {
    localStorage.removeItem(OPFS_FAILURE_KEY);
  } catch {
    // ignore
  }
}

/** Returns true if an OPFS runtime failure was recorded within the TTL. */
function hasRecentOPFSFailure(): boolean {
  try {
    const raw = localStorage.getItem(OPFS_FAILURE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < OPFS_FAILURE_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Detect iOS Safari (including iPadOS which reports as "MacIntel").
 * OPFS has known reliability issues on iOS Safari: worker suspension
 * when backgrounded, incomplete flush(), and repeated-operation failures.
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPhone, iPad, iPod directly in UA
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as "MacIntel" but has touch support
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Probe browser capabilities to determine the best storage backend.
 *
 * Returns `'opfs'` if the Origin Private File System is available,
 * otherwise falls back to `'indexeddb'`.
 *
 * On iOS Safari, always returns `'indexeddb'` due to known OPFS
 * reliability issues (worker suspension, data loss on backgrounding).
 *
 * Also skips OPFS if a recent runtime failure was recorded (within 24h),
 * to avoid re-selecting a backend that just crashed.
 *
 * Handles missing API, SecurityError in private browsing, and
 * any other unexpected errors gracefully.
 */
export async function selectStorageBackend(): Promise<StorageBackendType> {
  // iOS Safari has known OPFS reliability issues — prefer IndexedDB
  if (isIOSSafari()) {
    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'iOS Safari detected, using IndexedDB (OPFS unreliable on iOS)',
      level: 'info',
    });
    return 'indexeddb';
  }

  // Skip OPFS if it failed at runtime recently (within 24h)
  if (hasRecentOPFSFailure()) {
    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'Recent OPFS runtime failure detected, using IndexedDB',
      level: 'warning',
    });
    return 'indexeddb';
  }

  try {
    await navigator.storage.getDirectory();

    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'OPFS available',
      level: 'info',
    });

    return 'opfs';
  } catch {
    SentryService.addBreadcrumb({
      category: 'storage.select',
      message: 'OPFS not available, falling back to IndexedDB',
      level: 'info',
    });

    return 'indexeddb';
  }
}
